import {
  ActivityDayDetails,
  CRADashboardItemGQL,
  CRAStatus,
  GqlActivitySignature,
  InvoiceStatus,
  Maybe,
  Role,
} from '@bloomays-lib/types.shared';
import {
  convertActivityToEachDaysArray,
  convertDateToStringFormat,
  formatMonthYearFr,
  fixed,
  businessDaysInMonth,
  computeFreelanceAmountLines,
  computeFreelanceAmountParts,
  totalWorkedDaysInCra,
  type FreelanceAmountLine,
  type FreelanceAmountParts,
  type FreelanceWorkedDays,
} from '@bloomays-lib/utils.shared';
import type { DataGridV2Tone } from '@bloomays-lib/ui.shared/datagrid';
import uniq from 'lodash/fp/uniq';

// `businessDaysInMonth` vit désormais dans `utils.shared/date.utils` (réutilisable hors CRA) ;
// ré-exporté ici pour ne pas casser les imports existants.
export { businessDaysInMonth };

/**
 * Front-end derivations & KPI computations for the CRA dashboard (TEC-3038).
 *
 * The screen reuses the *business rules* of the existing CRA / activity page
 * (cf. `MonthlyActivityDashboard` + `ActivityDashboard`): the dataset is scoped
 * either **by mission** (`getCraByMission`) or **by month** (`getCraByMonth`),
 * and the eligibility of every action is driven by the domain `CRAStatus` and by
 * the two *distinct* invoicing cycles — client side and Bloomer side.
 *
 * The dashboard derives a view-model from `CRADashboardItemGQL`. The AM référent
 * comes from the mission `jobOwner` now resolved by the CRA query. A few cells of
 * the target mockup (montants HT facturés, jours prévus) are NOT carried by the
 * CRA GraphQL payload today, so they are modelled as optional enrichments: they
 * render when present (Storybook fixtures, future enriched query) and degrade
 * gracefully otherwise.
 */

/** Status displayed in the grid, mapped from the domain `CRAStatus` (charte v2). */
export type CraDisplayStatus = 'valide' | 'att-client' | 'att-bloomer' | 'non-soumis' | 'non-travaille';

/** Signature pill state, derived from the CRA status (`force` = forcé Bloomays). */
export type SignatureState = 'signe' | 'en-attente' | 'force' | 'annule';

/** Invoice pill state, mapped from the domain `InvoiceStatus` (one per cycle). */
export type FactState = 'finalisee' | 'a-valider' | 'en-cours' | 'non-creee' | 'en-erreur' | 'annulee' | 'a-creer';

/** NPS segment (0–10 scale): promoteur ≥ 9, passif 7–8, détracteur ≤ 6. */
export type NpsBucket = 'promoteur' | 'passif' | 'detracteur';

export const NPS_PROMOTER_MIN = 9;
export const NPS_DETRACTOR_MAX = 6;

export const npsBucket = (score: Maybe<number> | undefined): NpsBucket | null => {
  if (score === null || score === undefined) return null;
  if (score >= NPS_PROMOTER_MIN) return 'promoteur';
  if (score > NPS_DETRACTOR_MAX) return 'passif';
  return 'detracteur';
};

/** A CRA row enriched with everything the grid, the KPIs and the filters need. */
export type CraDashboardRow = CRADashboardItemGQL & {
  /** stable grid id (`missionId-activityId`, mirrors the legacy dashboard). */
  rowId: string;
  bloomer: string;
  client: string;
  mission: string;
  /** Account manager référent (mission jobOwner) — '' when unknown. */
  accountManager: string;
  /** AM email, used as the quick-filter key (null when unknown). */
  accountManagerEmail: string | null;
  displayStatus: CraDisplayStatus;
  bloomerSignature: SignatureState;
  clientSignature: SignatureState;
  /** jours CRA saisis (somme des jours travaillés, 0 si rien). */
  daysEntered: number;
  /** jours prévus (jours ouvrés du mois à défaut d'une valeur métier). */
  daysPlanned: number;
  clientFact: FactState;
  bloomerFact: FactState;
  /** Segment NPS client / Bloomer (null si pas de note) — alimente les filtres NPS. */
  clientNpsBucket: NpsBucket | null;
  bloomerNpsBucket: NpsBucket | null;

  // ── optional financials (absent du payload CRA aujourd'hui) ───────────────
  clientAmountHT?: number | null;
  bloomerAmountHT?: number | null;
  clientPaid?: boolean;
  bloomerPaid?: boolean;
};

// ── Status mapping (charte v2) ───────────────────────────────────────────────

const CRA_DISPLAY: Record<CRAStatus, CraDisplayStatus> = {
  'Validé client': 'valide',
  'Forcé Bloomays': 'valide',
  'Attente signature Client': 'att-client',
  'Attente signature Bloomer': 'att-bloomer',
  'Non créé': 'non-soumis',
  'Signature non créee': 'non-soumis',
  'Non travaillé': 'non-travaille',
};

export const CRA_CHIP: Record<CraDisplayStatus, { label: string; tone: DataGridV2Tone }> = {
  valide: { label: '✓ Validé', tone: 'success' },
  'att-client': { label: '⏳ Att. client', tone: 'warning' },
  'att-bloomer': { label: '⏳ Att. Bloomer', tone: 'info' },
  'non-soumis': { label: '— Non soumis', tone: 'danger' },
  'non-travaille': { label: '∅ Non travaillé', tone: 'neutral' },
};

/**
 * A CRA validated by Bloomays without the client validation (`Forcé Bloomays`)
 * shares the `valide` display status — KPIs, filters and action eligibility keep
 * treating it as validated — but it gets its own chip so a forced validation is
 * never read as a client validation (TEC-3242).
 */
export const FORCED_CRA_CHIP: { label: string; tone: DataGridV2Tone } = { label: '⚡ Forcé Bloomays', tone: 'purple' };

export const SIG_CHIP: Record<SignatureState, { label: string; tone: DataGridV2Tone }> = {
  signe: { label: 'Signé', tone: 'success' },
  'en-attente': { label: 'En attente', tone: 'neutral' },
  force: { label: '⚡ Forcé Bloomays', tone: 'purple' },
  annule: { label: 'Annulé', tone: 'neutral' },
};

export const FACT_CHIP: Record<FactState, { label: string; tone: DataGridV2Tone }> = {
  finalisee: { label: 'Finalisée', tone: 'success' },
  'a-valider': { label: 'À valider', tone: 'warning' },
  'a-creer': { label: 'À créer', tone: 'warning' },
  'en-cours': { label: 'En cours', tone: 'info' },
  'non-creee': { label: 'Non créée', tone: 'neutral' },
  'en-erreur': { label: 'En erreur', tone: 'danger' },
  annulee: { label: 'Annulée', tone: 'neutral' },
};

const INVOICE_FACT: Record<InvoiceStatus, FactState> = {
  'Créée et finalisée': 'finalisee',
  'Créée à valider': 'a-valider',
  'En cours': 'en-cours',
  'Non créée': 'non-creee',
  'A créer': 'a-creer',
  'En erreur': 'en-erreur',
  Annulée: 'annulee',
};

const factState = (status: InvoiceStatus): FactState => INVOICE_FACT[status] ?? 'non-creee';

/** A CRA is signed Bloomer-side once it has progressed past the Bloomer signature. */
const BLOOMER_SIGNED: ReadonlySet<CRAStatus> = new Set<CRAStatus>(['Attente signature Client', 'Validé client']);

/** Client-side signature is only effective when the client validated the CRA. */
const CLIENT_SIGNED: ReadonlySet<CRAStatus> = new Set<CRAStatus>(['Validé client']);

/**
 * CRA validated by Bloomays in place of the client, at the domain status level —
 * utilisable pendant la dérivation d'une ligne (avant que la ligne existe), là où
 * `isForcedCra` attend déjà une `CraDashboardRow`.
 */
const isForcedStatus = (status: CRAStatus): boolean => status === 'Forcé Bloomays';

/**
 * Signature pill for each side. On a `Forcé Bloomays` CRA, Bloomays validated
 * the CRA despite a missing signature: the presence of the parsed signature
 * info (`signingEmbed*Id`) tells whether the Bloomer actually signed before
 * being forced.
 */
const deriveBloomerSignature = (status: CRAStatus, signed: boolean): SignatureState => {
  // CRA non travaillé : aucune signature attendue → annulée plutôt qu'en attente.
  if (status === 'Non travaillé') return 'annule';
  if (isForcedStatus(status)) return signed ? 'signe' : 'force';
  return BLOOMER_SIGNED.has(status) ? 'signe' : 'en-attente';
};

/**
 * Côté client, un CRA `Forcé Bloomays` n'est jamais une validation client : le
 * statut signifie « validé sans les deux signatures » (cf. `resolveActivityStatus`),
 * la validation vient donc de Bloomays même quand un embed de signature existe
 * (posé par un Bloomie à la place du client). Le signataire réel reste visible
 * dans le tooltip du drawer — TEC-3242.
 */
const deriveClientSignature = (status: CRAStatus): SignatureState => {
  if (status === 'Non travaillé') return 'annule';
  if (isForcedStatus(status)) return 'force';
  return CLIENT_SIGNED.has(status) ? 'signe' : 'en-attente';
};

// ── Row derivation ───────────────────────────────────────────────────────────

/**
 * Optional enrichment a caller (enriched query / fixtures) can layer on top of a
 * raw CRA item — financial figures the CRA GraphQL doesn't expose yet.
 */
export type CraRowEnrichment = {
  clientAmountHT?: number | null;
  bloomerAmountHT?: number | null;
  clientPaid?: boolean;
  bloomerPaid?: boolean;
};

const accountManagerName = (item: CRADashboardItemGQL): string => {
  const jo = item.jobOwner;
  if (!jo) return '';
  return `${jo.firstname ?? ''} ${jo.lastname ?? ''}`.trim();
};

export const deriveCraRow = (item: CRADashboardItemGQL, enrichment: CraRowEnrichment = {}): CraDashboardRow => {
  const month = item.activityMonth ? new Date(item.activityMonth) : null;
  return {
    ...item,
    rowId: `${item.missionId}-${item.activityId ?? 'na'}`,
    bloomer: item.bloomerFullName || '',
    client: item.clientSocietyName || '',
    mission: item.missionLabel || '',
    accountManager: accountManagerName(item),
    accountManagerEmail: item.jobOwner?.email ?? null,
    displayStatus: CRA_DISPLAY[item.status] ?? 'non-soumis',
    bloomerSignature: deriveBloomerSignature(item.status, !!item.bloomerSignatureInfo),
    clientSignature: deriveClientSignature(item.status),
    daysEntered: fixed(
      totalWorkedDaysInCra({
        normalWorkedDays: item.nbDaysWorked ?? 0,
        saturdaysWorkedDays: item.nbDaysMarkupWorked ?? 0,
        sundayAndholidaysWorkedDays: item.nbDaysMarkup2Worked ?? 0,
        astreinte: item.nbDaysAstreinteWorked ?? 0,
      }),
    ),
    daysPlanned: month ? businessDaysInMonth(month) : 0,
    clientFact: factState(item.clientInvoiceStatus),
    bloomerFact: factState(item.bloomerInvoiceStatus),
    clientNpsBucket: npsBucket(item.clientScore),
    bloomerNpsBucket: npsBucket(item.bloomerScore),
    clientAmountHT: enrichment.clientAmountHT ?? null,
    bloomerAmountHT: enrichment.bloomerAmountHT ?? null,
    clientPaid: enrichment.clientPaid ?? false,
    bloomerPaid: enrichment.bloomerPaid ?? false,
  };
};

// ── Predicates reused by tabs, filters and action eligibility ────────────────

export const isAlert = (row: CraDashboardRow): boolean => row.displayStatus === 'non-soumis';
export const isInProgress = (row: CraDashboardRow): boolean =>
  row.displayStatus === 'att-client' || row.displayStatus === 'att-bloomer';
export const isValidated = (row: CraDashboardRow): boolean => row.displayStatus === 'valide';

/** CRA validated by Bloomays in place of the client (`Forcé Bloomays`). */
export const isForcedCra = (row: CraDashboardRow): boolean => isForcedStatus(row.status);

/**
 * Status chip of a row — single source of truth for the grid, the detail drawer
 * and the CSV export. A forced CRA surfaces its own chip instead of the generic
 * "✓ Validé" one (TEC-3242).
 */
export const craStatusChip = (row: CraDashboardRow): { label: string; tone: DataGridV2Tone } =>
  isForcedCra(row) ? FORCED_CRA_CHIP : CRA_CHIP[row.displayStatus];

/**
 * Action eligibility — mirrors the rules enforced by the legacy `ActivityDashboard`:
 * forcing is only offered while the CRA isn't validated yet, relances follow the
 * signature waiting state, and each invoice retry follows its own cycle.
 */
const VALIDATED_STATUSES: ReadonlySet<CRAStatus> = new Set<CRAStatus>([
  'Validé client',
  'Forcé Bloomays',
  'Non travaillé',
]);
const BLOOMER_RELAUNCH_STATUSES: ReadonlySet<CRAStatus> = new Set<CRAStatus>([
  'Non créé',
  'Signature non créee',
  'Attente signature Bloomer',
]);
/** Invoice states that expose a retry/create action in the legacy dashboard. */
const RETRYABLE_INVOICE: ReadonlySet<InvoiceStatus> = new Set<InvoiceStatus>([
  'A créer',
  'En erreur',
  'Créée à valider',
  'Créée et finalisée',
]);

export const canForceCRA = (row: CraDashboardRow): boolean => !VALIDATED_STATUSES.has(row.status);
export const canRelaunchClient = (row: CraDashboardRow): boolean =>
  row.status === 'Attente signature Client' && !!row.signingDocumentId;
export const canRelaunchBloomer = (row: CraDashboardRow): boolean => BLOOMER_RELAUNCH_STATUSES.has(row.status);
export const canRetryClientInvoice = (row: CraDashboardRow): boolean => RETRYABLE_INVOICE.has(row.clientInvoiceStatus);
export const canRetryBloomerInvoice = (row: CraDashboardRow): boolean => {
  if (RETRYABLE_INVOICE.has(row.bloomerInvoiceStatus)) return true;
  // mirrors `couldBeCreated`: a non-created Bloomer invoice can be created once the
  // CRA is validated and a Bloomer invoice file is available.
  return (
    row.bloomerInvoiceStatus === 'Non créée' &&
    !!row.invoiceURL &&
    (row.status === 'Validé client' || row.status === 'Forcé Bloomays')
  );
};

/**
 * Offers the (re)upload of the Bloomer invoice file. Stays available even once paid
 * (the supplier-invoice generation retry is idempotent), so a corrected / different
 * invoice can always be re-uploaded.
 *
 * Bloomies (Admin) may also store an invoice received before the CRA is validated — or
 * before it even exists (TEC-3262) — which mirrors the rule enforced by `uploadActivityFile`.
 */
export const canUploadBloomerInvoice = (row: CraDashboardRow, roles: Role[] = []): boolean =>
  roles.includes(Role.Admin) || isValidated(row);

/** Same rule as the Bloomer invoice, for the (optional, manual) additional invoice. */
export const canUploadAdditionalInvoice = (row: CraDashboardRow, roles: Role[] = []): boolean =>
  roles.includes(Role.Admin) || isValidated(row);

const CRA_DAYS_EDIT_STATUSES: ReadonlySet<CRAStatus> = new Set<CRAStatus>([
  'Validé client',
  'Forcé Bloomays',
  'Non travaillé',
]);

/** Manual CRA days edit — admin only (v1), eligible CRA statuses only. */
export const canEditCraDays = (row: CraDashboardRow, roles: Role[] = []): boolean =>
  roles.includes(Role.Admin) && CRA_DAYS_EDIT_STATUSES.has(row.status);

/** A CRA that has an additional Bloomer invoice uploaded — feeds the quick filter. */
export const hasAdditionalInvoice = (row: CraDashboardRow): boolean => !!row.additionalInvoiceURL;

/** A CRA still awaiting validation (forçable) — drives the "CRA non validés" focus. */
export const needsCraValidation = (row: CraDashboardRow): boolean => canForceCRA(row);

/**
 * Client invoice needing a human action: generation error, or "à valider" while
 * not yet billed (`clientInvoiceBilled`).
 */
export const clientInvoiceNeedsAction = (row: CraDashboardRow): boolean =>
  row.clientInvoiceStatus === 'En erreur' ||
  (row.clientInvoiceStatus === 'Créée à valider' && !row.clientInvoiceBilled);

/**
 * Bloomer invoice needing a human action: generation error, or "à valider" while
 * not yet paid (`bloomerInvoiceBilled` maps to the Bloomer payment flag).
 */
export const bloomerInvoiceNeedsAction = (row: CraDashboardRow): boolean =>
  row.bloomerInvoiceStatus === 'En erreur' ||
  (row.bloomerInvoiceStatus === 'Créée à valider' && !row.bloomerInvoiceBilled);

/** Either invoice cycle (client or Bloomer) needs a human action — "factures à traiter". */
export const needsInvoiceAction = (row: CraDashboardRow): boolean =>
  clientInvoiceNeedsAction(row) || bloomerInvoiceNeedsAction(row);

// ── KPI aggregation ──────────────────────────────────────────────────────────

export type CraKpis = {
  total: number;
  validatedCount: number;
  notValidatedCount: number;
  completionPct: number; // 0..1
  alertCount: number;
  invoiceActionCount: number;
  /** jours travaillés / prévus cumulés + taux. */
  workedDaysTotal: number;
  plannedDaysTotal: number;
  daysCompletionPct: number; // 0..1
  /** CA vente HT / coût achat HT cumulés du périmètre (lignes dont le TJM est connu). */
  salesAmountHT: number;
  purchaseAmountHT: number;
  /** NPS client / Bloomer (-100..100) + répartition promoteurs / passifs / détracteurs. */
  clientNps: NpsStats;
  bloomerNps: NpsStats;
  bloomerSignedCount: number;
  clientSignedCount: number;
  relanceBloomerCount: number;
  relanceClientCount: number;
};

const sum = (values: number[]): number => values.reduce((acc, value) => acc + value, 0);

/** NPS aggregation (score -100..100 + segment counts) over a list of scores. */
export type NpsStats = { score: number; scored: number; promoters: number; passives: number; detractors: number };

const npsStats = (scores: number[]): NpsStats => {
  const scored = scores.length;
  const promoters = scores.filter((s) => s >= NPS_PROMOTER_MIN).length;
  const detractors = scores.filter((s) => s <= NPS_DETRACTOR_MAX).length;
  return {
    scored,
    promoters,
    passives: scored - promoters - detractors,
    detractors,
    score: scored ? Math.round(((promoters - detractors) / scored) * 100) : 0,
  };
};

const isScore = (s: Maybe<number> | undefined): s is number => s !== null && s !== undefined;

export const computeCraKpis = (rows: CraDashboardRow[]): CraKpis => {
  const total = rows.length;
  const validated = rows.filter(isValidated);

  const workedDaysTotal = sum(rows.map((r) => r.daysEntered));
  const plannedDaysTotal = sum(rows.map((r) => r.daysPlanned));
  const salesAmountHT = round2(sum(rows.map((r) => craSalesAmountHT(r) ?? 0)));
  const purchaseAmountHT = round2(sum(rows.map((r) => craPurchaseAmountHT(r) ?? 0)));

  return {
    total,
    validatedCount: validated.length,
    notValidatedCount: rows.filter(needsCraValidation).length,
    completionPct: total ? validated.length / total : 0,
    alertCount: rows.filter(isAlert).length,
    invoiceActionCount: rows.filter(needsInvoiceAction).length,
    workedDaysTotal,
    plannedDaysTotal,
    daysCompletionPct: plannedDaysTotal ? workedDaysTotal / plannedDaysTotal : 0,
    salesAmountHT,
    purchaseAmountHT,
    clientNps: npsStats(rows.map((r) => r.clientScore).filter(isScore)),
    bloomerNps: npsStats(rows.map((r) => r.bloomerScore).filter(isScore)),
    bloomerSignedCount: rows.filter((r) => r.bloomerSignature === 'signe').length,
    clientSignedCount: rows.filter((r) => r.clientSignature === 'signe').length,
    relanceBloomerCount: rows.filter(canRelaunchBloomer).length,
    relanceClientCount: rows.filter(canRelaunchClient).length,
  };
};

// ── Montants CRA (vente / achat, split normal / HNO) ─────────────────────────

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** Jours ouvrés (jours travaillés normaux) d'une ligne CRA. */
const workedDaysOf = (row: CraDashboardRow): number => row.nbDaysWorked ?? 0;

/** Jours HNO (heures non ouvrées) d'une ligne : samedi + dimanche/férié + astreinte. */
const hnoDaysOf = (row: CraDashboardRow): number =>
  (row.nbDaysMarkupWorked ?? 0) + (row.nbDaysMarkup2Worked ?? 0) + (row.nbDaysAstreinteWorked ?? 0);

/** Répartition des jours d'une ligne au format attendu par le helper de montants. */
const craWorkedDays = (row: CraDashboardRow): FreelanceWorkedDays => ({
  normalWorkedDays: row.nbDaysWorked ?? 0,
  saturdaysWorkedDays: row.nbDaysMarkupWorked ?? 0,
  sundayAndholidaysWorkedDays: row.nbDaysMarkup2Worked ?? 0,
  astreinte: row.nbDaysAstreinteWorked ?? 0,
});

/**
 * Montant vente HT d'une ligne, décomposé normal / HNO, via la méthode de calcul
 * partagée (coefficients HNO 1,5 / 2 / astreinteCoef). `null` si le TJM de vente
 * est absent du payload (distingue « 0 € » d'un montant non calculable).
 */
export const craSalesParts = (row: CraDashboardRow): FreelanceAmountParts | null => {
  const price = row.salePricePerDayWithoutTaxes;
  if (price === null || price === undefined) return null;
  return computeFreelanceAmountParts(craWorkedDays(row), price, row.astreinteCoef ?? undefined);
};

/** Coût achat HT d'une ligne, décomposé normal / HNO. `null` si le TJM d'achat est absent. */
export const craPurchaseParts = (row: CraDashboardRow): FreelanceAmountParts | null => {
  const price = row.purchasePricePerDayWithoutTaxes;
  if (price === null || price === undefined) return null;
  return computeFreelanceAmountParts(craWorkedDays(row), price, row.astreinteCoef ?? undefined);
};

/** CA vente HT total d'une ligne (null si TJM inconnu). */
export const craSalesAmountHT = (row: CraDashboardRow): number | null => craSalesParts(row)?.total ?? null;

/** Coût achat HT total d'une ligne (null si TJM inconnu). */
export const craPurchaseAmountHT = (row: CraDashboardRow): number | null => craPurchaseParts(row)?.total ?? null;

/** Décomposition complète des montants d'une ligne — alimente le drawer de détail. */
export type CraAmountBreakdown = {
  normalDays: number;
  hnoDays: number;
  /** Montant vente HT (normal / HNO / total), null si TJM vente inconnu. */
  sales: FreelanceAmountParts | null;
  /** Coût achat HT (normal / HNO / total), null si TJM achat inconnu. */
  purchase: FreelanceAmountParts | null;
};

export const craAmountBreakdown = (row: CraDashboardRow): CraAmountBreakdown => ({
  normalDays: fixed(workedDaysOf(row)),
  hnoDays: fixed(hnoDaysOf(row)),
  sales: craSalesParts(row),
  purchase: craPurchaseParts(row),
});

/**
 * Détail du montant d'achat HT attendu (ce que Bloomays doit retrouver sur la facture
 * fournisseur), composante par composante : la méthode de calcul et ses coefficients HNO
 * restent dans `computeFreelanceAmountLines` (utils partagés), on ne garde ici que la
 * lecture — composantes non déclarées écartées et arrondi d'affichage. Vide si le TJM
 * d'achat du mois est inconnu.
 */
export const craExpectedPurchaseLines = (row: CraDashboardRow): FreelanceAmountLine[] => {
  const price = row.purchasePricePerDayWithoutTaxes;
  if (price === null || price === undefined) return [];
  return computeFreelanceAmountLines(craWorkedDays(row), price, row.astreinteCoef ?? undefined)
    .filter((line) => line.days > 0)
    .map((line) => ({ ...line, days: fixed(line.days), amount: fixed(line.amount, 2) }));
};

// ── Selection recap (traitement par lot) ─────────────────────────────────────

const emptyParts = (): FreelanceAmountParts => ({ normal: 0, hno: 0, total: 0 });

const addParts = (acc: FreelanceAmountParts, p: FreelanceAmountParts): void => {
  acc.normal += p.normal;
  acc.hno += p.hno;
  acc.total += p.total;
};

const roundParts = (p: FreelanceAmountParts): FreelanceAmountParts => ({
  normal: round2(p.normal),
  hno: round2(p.hno),
  total: round2(p.total),
});

/** Récap agrégé d'une sélection de CRA — alimente la barre de traitement par lot. */
export type CraSelectionSummary = {
  /** Nombre de CRA sélectionnés. */
  count: number;
  /** Total des jours ouvrés (jours travaillés normaux). */
  workedDays: number;
  /** Total des jours HNO (samedi + dimanche/férié + astreinte). */
  hnoDays: number;
  /** CA vente HT cumulé (normal / HNO / total) sur les lignes dont le TJM vente est connu. */
  sales: FreelanceAmountParts;
  /** Coût achat HT cumulé (normal / HNO / total) sur les lignes dont le TJM achat est connu. */
  purchase: FreelanceAmountParts;
  /** Lignes avec des jours saisis mais sans TJM vente : montant non calculable. */
  missingPricingCount: number;
};

/**
 * Agrège une sélection de lignes CRA : jours ouvrés / HNO et montants vente & achat
 * (décomposés normal / HNO) via la méthode de calcul partagée. Fonction pure.
 */
export const computeCraSelectionSummary = (rows: CraDashboardRow[]): CraSelectionSummary => {
  let workedDays = 0;
  let hnoDays = 0;
  let missingPricingCount = 0;
  const sales = emptyParts();
  const purchase = emptyParts();

  rows.forEach((row) => {
    const worked = workedDaysOf(row);
    const hno = hnoDaysOf(row);
    workedDays += worked;
    hnoDays += hno;

    const salesParts = craSalesParts(row);
    if (salesParts) addParts(sales, salesParts);
    // Tarif vente inconnu sur un CRA réellement saisi : montant non calculable.
    else if (worked + hno > 0) missingPricingCount += 1;

    const purchaseParts = craPurchaseParts(row);
    if (purchaseParts) addParts(purchase, purchaseParts);
  });

  return {
    count: rows.length,
    workedDays: fixed(workedDays),
    hnoDays: fixed(hnoDays),
    sales: roundParts(sales),
    purchase: roundParts(purchase),
    missingPricingCount,
  };
};

// ── Quick-filter option helpers ──────────────────────────────────────────────

export type CraTab = 'tous' | 'alertes' | 'en-cours' | 'valides';

export type AmOption = { value: string; label: string };

/** Dédupe + sort the AMs present in the rows — feeds the "Tous les AM" filter. */
export const buildAmOptions = (rows: CraDashboardRow[]): AmOption[] => {
  const byEmail = new Map<string, string>();
  rows.forEach((row) => {
    if (row.accountManagerEmail) {
      byEmail.set(row.accountManagerEmail, row.accountManager || row.accountManagerEmail);
    }
  });
  return [...byEmail.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'fr'));
};

export const craStatusOptions: Array<{ value: CraDisplayStatus; label: string }> = (
  Object.keys(CRA_CHIP) as CraDisplayStatus[]
).map((key) => ({ value: key, label: CRA_CHIP[key].label }));

export const factStatusOptions: Array<{ value: FactState; label: string }> = (
  Object.keys(FACT_CHIP) as FactState[]
).map((key) => ({ value: key, label: FACT_CHIP[key].label }));

/** Options du filtre NPS client (segment de la note client). */
export const npsBucketOptions: Array<{ value: NpsBucket; label: string }> = [
  { value: 'promoteur', label: '😀 Promoteurs' },
  { value: 'passif', label: '😐 Passifs' },
  { value: 'detracteur', label: '😞 Détracteurs' },
];

/** Maybe helper re-export so consumers don't import the domain type for nothing. */
export type { Maybe };

// ── Références BDC portées par un CRA ────────────────────────────────────────

/**
 * Séparateur des segments de BDC dénormalisés sur le CRA : à la facturation, l'API concatène
 * le BDC retenu pour la facture régulière et celui de la facture HNO (cf.
 * `createActivityCustomerInvoice`, api.bloomers). Un numéro de BDC pouvant lui-même contenir
 * un « / » (ex. « BDC 2024/042 »), le séparateur est bien « espace slash espace ».
 */
const CRA_ORDER_NUMBER_SEPARATOR = ' / ';

/**
 * Segment qui n'est pas un numéro de BDC : quand la facturation est forcée sans BDC résolu,
 * l'API conserve le motif d'échec à la place du numéro, préfixé par le type de facture
 * (« Facture HNO : BDC absent »). Un tel segment reste affiché, mais n'est pas deeplinkable.
 */
const CRA_ORDER_NUMBER_FAILURE_SEGMENT = /\s:\s/;

/** Référence de BDC affichée dans le détail d'un CRA. */
export type CraPurchaseOrderReference = {
  /** Texte affiché : numéro de BDC, ou motif d'échec quand la facturation a été forcée sans BDC. */
  label: string;
  /** Numéro de BDC à deeplinker — absent quand le segment n'est pas un numéro. */
  orderNumber?: string;
};

/**
 * Une référence par BDC porté par le CRA, dédupliquée.
 *
 * Un CRA facturé peut référencer deux BDC (facture régulière + facture HNO), voire deux fois
 * le même quand un seul BDC couvre les deux : on n'expose alors qu'une seule référence. Le
 * champ dénormalisé ne doit jamais être deeplinké tel quel, la recherche du dashboard BDC ne
 * retrouvant aucun BDC pour deux numéros concaténés.
 */
export const craPurchaseOrderReferences = (orderNumber: Maybe<string> | undefined): CraPurchaseOrderReference[] => {
  const segments = uniq(
    (orderNumber ?? '')
      .split(CRA_ORDER_NUMBER_SEPARATOR)
      .map((segment) => segment.trim())
      .filter((segment) => segment !== ''),
  );
  return segments.map((label) =>
    CRA_ORDER_NUMBER_FAILURE_SEGMENT.test(label) ? { label } : { label, orderNumber: label },
  );
};

/** Signing document id when `url` targets the public CRA HTML page (`/public/cra/:id/cra.pdf`). */
export const parsePublicCraSigningDocumentId = (url: string): string | null => {
  try {
    const pathname = url.startsWith('http') ? new URL(url).pathname : url;
    const match = pathname.match(/\/public\/cra\/([^/]+)\/cra\.pdf$/);
    return match?.[1] ?? null;
  } catch {
    return null;
  }
};

// ── Vérification du CRA (popin de contrôle) ──────────────────────────────────

/**
 * Précision d'affichage des jours de CRA : celle de `fixed`, qui normalise déjà les
 * quantités du domaine (jours déclarés, astreinte, totaux).
 */
const CRA_DAYS_DECIMALS = 4;

/**
 * Quantité de jours en français, sans arrondi de lecture : un CRA se déclare au quart ou
 * au huitième de journée (0,375 j = 3 h sur 8 h, 17,875 j de total), qu'un affichage à deux
 * décimales fausserait (0,38 j / 17,88 j) au moment même où l'on contrôle la facture.
 * `fixed` gomme les artefacts de calcul flottant avant formatage.
 *
 * @param minimumFractionDigits décimales minimales (2 pour aligner une colonne de totaux).
 */
export const formatCraDays = (value: number, minimumFractionDigits = 0): string =>
  fixed(value).toLocaleString('fr-FR', { minimumFractionDigits, maximumFractionDigits: CRA_DAYS_DECIMALS });

/**
 * Détails de signature lisibles (date · signataire · IP) — alimente les tooltips
 * des pastilles de signature (drawer de détail, popin de vérification du CRA).
 */
export const craSignatureTooltip = (info: Maybe<GqlActivitySignature> | undefined): string | undefined => {
  if (!info) return undefined;
  const parts: string[] = [];
  if (info.signedAt) parts.push(`Signé le ${convertDateToStringFormat(new Date(info.signedAt), 'dd/MM/yyyy à HH:mm')}`);
  if (info.signerName) parts.push(info.signerName);
  if (info.ip) parts.push(`IP ${info.ip}`);
  return parts.length ? parts.join(' · ') : undefined;
};

/** Service de géolocalisation d'adresse IP ouvert depuis les informations de signature. */
const IP_GEOLOCATION_BASE_URL = 'https://ipinfo.io';

/** Une adresse IP v4 ou v6 : uniquement des chiffres hexadécimaux, points et deux-points. */
const IP_ADDRESS_PATTERN = /^[0-9a-fA-F.:]+$/;

/**
 * Lien de géolocalisation de l'adresse IP relevée à la signature (v4 ou v6). `null`
 * quand l'IP est absente ou n'a pas la forme d'une adresse : la valeur vient d'un
 * champ texte, on ne la place jamais dans une URL sans la vérifier.
 */
export const craSignatureIpUrl = (ip: Maybe<string> | undefined): string | null => {
  const value = ip?.trim();
  return value && IP_ADDRESS_PATTERN.test(value) ? `${IP_GEOLOCATION_BASE_URL}/${value}` : null;
};

/**
 * Un CRA est vérifiable dès qu'une activité est saisie sur le mois : la popin de
 * vérification rejoue le CRA (jours déclarés + signatures) en lecture seule, ce
 * qui suppose un mois et le détail des jours.
 */
export const canVerifyCra = (row: CraDashboardRow): boolean =>
  !!row.activityId && !!row.activityMonth && !!row.nbDaysDetails;

/**
 * Lecture de contrôle d'un CRA : le mois rejoué jour par jour (même conversion que
 * la grille calendaire du tableau de bord mission) et la décomposition des jours
 * déclarés à confronter à la facture fournisseur.
 */
export type CraVerificationDetail = {
  /** Mois du CRA, ex. « Mars 2025 ». */
  monthLabel: string;
  /** Journées du mois (quantité déclarée, astreinte, commentaire, férié / week-end). */
  days: ActivityDayDetails[];
  /** Jours travaillés en semaine. */
  workedDays: number;
  saturdayDays: number;
  sundayHolidayDays: number;
  astreinteDays: number;
  /** Total déclaré du CRA (identique à la colonne « Jours CRA » de la grille). */
  totalDays: number;
  /** Nombre de journées commentées par le Bloomer — pistes de contrôle. */
  commentedDaysCount: number;
  /** Journées déclarées partiellement (moins d'une journée) — explique un total non entier. */
  partialDaysCount: number;
  /** TJM d'achat contractuel du mois (montant reversé au Bloomer), `null` si inconnu. */
  purchasePricePerDay: number | null;
  /**
   * Montant HT attendu sur la facture fournisseur : jours déclarés au TJM d'achat,
   * coefficients HNO appliqués. `null` si le TJM d'achat est inconnu.
   */
  expectedPurchaseAmountHT: number | null;
};

/**
 * Vue de contrôle d'une ligne du tableau de bord. `null` quand il n'y a pas de CRA
 * rejouable (cf. `canVerifyCra`) ou que le détail des jours n'est pas exploitable.
 */
export const craRowVerificationDetail = (row: CraDashboardRow): CraVerificationDetail | null => {
  if (!canVerifyCra(row) || !row.activityMonth || !row.nbDaysDetails) return null;
  const month = new Date(row.activityMonth);
  let days: ActivityDayDetails[];
  try {
    days = convertActivityToEachDaysArray(month.toISOString(), row.nbDaysDetails);
  } catch {
    // Détail des jours non exploitable (JSON invalide) : rien à rejouer.
    return null;
  }
  return {
    monthLabel: formatMonthYearFr(month),
    days,
    workedDays: fixed(row.nbDaysWorked ?? 0),
    saturdayDays: fixed(row.nbDaysMarkupWorked ?? 0),
    sundayHolidayDays: fixed(row.nbDaysMarkup2Worked ?? 0),
    astreinteDays: fixed(row.nbDaysAstreinteWorked ?? 0),
    totalDays: row.daysEntered,
    commentedDaysCount: days.filter((day) => !!day.comment).length,
    partialDaysCount: days.filter((day) => (day.dayWorked ?? 0) > 0 && (day.dayWorked ?? 0) < 1).length,
    purchasePricePerDay: row.purchasePricePerDayWithoutTaxes ?? null,
    expectedPurchaseAmountHT: craPurchaseAmountHT(row),
  };
};

// ── CSV export ───────────────────────────────────────────────────────────────

/** Colonnes exportées (libellé + accesseur de la valeur "humaine" de la ligne). */
const CSV_COLUMNS: ReadonlyArray<{ header: string; value: (row: CraDashboardRow) => string }> = [
  {
    header: 'Mois',
    value: (r) => (r.activityMonth ? convertDateToStringFormat(new Date(r.activityMonth), 'MM/yyyy') : ''),
  },
  { header: 'Mission', value: (r) => r.mission },
  { header: 'Bloomer', value: (r) => r.bloomer },
  { header: 'Client', value: (r) => r.client },
  { header: 'AM référent', value: (r) => r.accountManager },
  { header: 'Statut CRA', value: (r) => craStatusChip(r).label },
  { header: 'Signature Bloomer', value: (r) => SIG_CHIP[r.bloomerSignature].label },
  { header: 'Signature client', value: (r) => SIG_CHIP[r.clientSignature].label },
  { header: 'Jours saisis', value: (r) => String(r.daysEntered) },
  { header: 'Jours prévus', value: (r) => String(r.daysPlanned) },
  { header: 'Fact. client', value: (r) => FACT_CHIP[r.clientFact].label },
  { header: 'Fact. Bloomer', value: (r) => FACT_CHIP[r.bloomerFact].label },
  { header: 'NPS client', value: (r) => (r.clientScore == null ? '' : String(r.clientScore)) },
  { header: 'NPS Bloomer', value: (r) => (r.bloomerScore == null ? '' : String(r.bloomerScore)) },
];

/** Échappe une cellule CSV (RFC 4180 : guillemets doublés si , " ; ou saut de ligne). */
const csvCell = (value: string): string => (/[",;\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

/**
 * Sérialise les lignes sélectionnées en CSV (séparateur `;`, lisible par Excel FR).
 * Fonction pure — le composant se charge du téléchargement.
 */
export const craRowsToCsv = (rows: CraDashboardRow[]): string => {
  const header = CSV_COLUMNS.map((c) => csvCell(c.header)).join(';');
  const body = rows.map((row) => CSV_COLUMNS.map((c) => csvCell(c.value(row))).join(';'));
  return [header, ...body].join('\r\n');
};
